import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { Phone, Zap } from "lucide-react";
import { CONTACT, IMG } from "@/lib/site";
import { scrollToSection } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay, className = "" }) {
  return (
    <div className="overflow-hidden">
      <motion.div
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, delay, ease: EASE }}
        className={className}
      >
        {children}
      </motion.div>
    </div>
  );
}

const STATS = [
  { v: "30", u: "MIN", label: "réparation express en moyenne" },
  { v: "1", u: "AN", label: "garantie pièces & main d'œuvre" },
  { v: "100", u: "%", label: "devis gratuit, sans engagement" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const auraY = useTransform(scrollYProgress, [0, 1], [0, 140]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 14 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="hero" ref={ref} className="grid-lines relative overflow-hidden pb-16 pt-32 sm:pt-36 lg:pb-24 lg:pt-40">
      <motion.div style={{ y: auraY }} className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute left-1/2 top-[-320px] h-[640px] w-[900px] -translate-x-1/2 rounded-full bg-neon/15 blur-[140px]" />
        <div className="absolute -left-40 bottom-0 h-[420px] w-[420px] rounded-full bg-neonblue/10 blur-[120px]" />
      </motion.div>

      <div className="relative mx-auto grid w-full max-w-7xl items-center gap-14 px-5 lg:grid-cols-[1.15fr_0.85fr] lg:gap-10 lg:px-8">
        <div>
          <MaskedLine delay={0.1}>
            <div className="mb-6 flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-2 rounded-full border border-neon/30 bg-neon/10 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-neon">
                <span className="h-1.5 w-1.5 animate-pulse-glow rounded-full bg-neon" />
                Saint-Pierre-en-Faucigny · Haute-Savoie
              </span>
            </div>
          </MaskedLine>

          <h1 className="font-display font-black uppercase leading-[0.9] tracking-tight text-[clamp(3.4rem,10vw,7.5rem)]">
            <MaskedLine delay={0.22} className="text-white">Réparez.</MaskedLine>
            <MaskedLine delay={0.36} className="text-outline-cyan">Ne remplacez</MaskedLine>
            <MaskedLine delay={0.5} className="text-neon">pas.</MaskedLine>
          </h1>

          <MaskedLine delay={0.66}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Smartphones, tablettes et ordinateurs : devis gratuit, réparation express
              en atelier et garantie 1 an. Au cœur de la Haute-Savoie. Vous validez le devis, on répare, vous payez après.
            </p>
          </MaskedLine>

          <MaskedLine delay={0.78}>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-cta-devis"
                onClick={() => scrollToSection("#devis")}
                className="flex items-center gap-2 rounded-full bg-neon px-7 py-4 text-sm font-extrabold uppercase tracking-wider text-night chip-glow transition-transform duration-200 hover:scale-[1.04]"
              >
                <Zap size={17} />
                Demander un devis gratuit
              </button>
              <a
                data-testid="hero-cta-phone"
                href={CONTACT.phoneHref}
                className="flex items-center gap-2 rounded-full border border-line px-7 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:border-neon hover:text-neon"
              >
                <Phone size={17} className="text-neon" />
                {CONTACT.phoneDisplay}
              </a>
            </div>
          </MaskedLine>

          <MaskedLine delay={0.9}>
            <div className="mt-9 flex flex-wrap gap-x-6 gap-y-2 text-[12px] font-semibold text-slate-400">
              <span className="flex items-center gap-2"><span aria-hidden="true" className="font-bold text-neon">✓</span> Garantie 1 an</span>
              <span className="flex items-center gap-2"><span aria-hidden="true" className="font-bold text-neon">✓</span> Pièces qualité certifiées</span>
              <span className="flex items-center gap-2"><span aria-hidden="true" className="font-bold text-neon">✓</span> Devis en 1 minute</span>
              <span className="flex items-center gap-2"><span aria-hidden="true" className="font-bold text-neon">✓</span> Paiement après validation du devis</span>
            </div>
          </MaskedLine>
        </div>

        <motion.div style={{ y: imgY }} className="relative hidden lg:block">
          <motion.div
            onMouseMove={onMove}
            onMouseLeave={() => { mx.set(0); my.set(0); }}
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            className="relative [perspective:1200px]"
          >
            <div className="absolute -inset-6 rounded-[32px] bg-neon/20 blur-3xl" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-[28px] border border-line bg-ink">
              <img
                src={IMG.hero}
                alt="Technicien MAXREVIX réparant un smartphone en atelier"
                className="h-[520px] w-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-night via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 rounded-2xl border border-line bg-night/80 px-5 py-4 backdrop-blur-xl">
                <p className="font-display text-3xl font-extrabold uppercase text-neon">30 min</p>
                <p className="text-xs text-slate-400">Le temps d'un café, votre appareil est réparé.</p>
              </div>
            </div>

            <div className="absolute -right-6 -top-6 animate-float rounded-2xl border border-neon/30 bg-night/90 px-5 py-4 backdrop-blur-xl">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon">Atelier local</p>
              <p className="mt-1 text-sm font-bold text-white">Saint-Pierre-en-Faucigny · 74800</p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <div className="relative mx-auto mt-16 grid w-full max-w-7xl grid-cols-1 gap-px overflow-hidden rounded-2xl border border-line bg-line px-0 sm:grid-cols-3 lg:mx-auto lg:mt-24">
        {STATS.map((s) => (
          <div key={s.u + s.v} className="bg-ink px-8 py-7">
            <p className="font-display text-5xl font-extrabold text-white">
              {s.v}<span className="text-neon">{s.u}</span>
            </p>
            <p className="mt-2 text-sm text-slate-400">{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
